import React from 'react';
import { motion } from 'framer-motion';
import { Wrench, Clock, Mail } from 'lucide-react';

export const MaintenancePage = ({ message, language = 'en' }) => {
  const getTranslatedText = (textObj) => {
    return textObj?.[language] || textObj?.en || '';
  };

  const titles = {
    ro: 'Site în Mentenanță',
    en: 'Site Under Maintenance',
    fr: 'Site en Maintenance'
  };

  const subtitles = {
    ro: 'Lucrăm pentru a vă oferi o experiență mai bună',
    en: 'We are working to give you a better experience',
    fr: 'Nous travaillons pour vous offrir une meilleure expérience'
  };

  const backSoon = {
    ro: 'Revenim în curând!',
    en: "We'll be back soon!",
    fr: 'Nous serons bientôt de retour!'
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4a942' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />

      <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-serif tracking-[0.3em] text-primary">
            RENTIASI.RO
          </h1>
          <p className="text-sm text-muted-foreground mt-2 tracking-wider">
            Apartamente de Închiriat în Iași
          </p>
        </motion.div>

        {/* Maintenance Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <div className="relative inline-block">
            <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
              <Wrench size={48} className="text-primary animate-pulse" />
            </div>
            {/* Spinning gear effect */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              className="absolute -top-2 -right-2 w-12 h-12 rounded-full bg-slate-900 border border-primary/30 flex items-center justify-center"
            >
              <Clock size={20} className="text-primary" />
            </motion.div>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-3xl md:text-4xl font-serif text-foreground mb-4"
        >
          {titles[language] || titles.en}
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-lg text-muted-foreground mb-6"
        >
          {subtitles[language] || subtitles.en}
        </motion.p>

        {/* Custom Message */}
        {message && getTranslatedText(message) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="glass p-6 mb-8"
          >
            <p className="text-foreground">
              {getTranslatedText(message)}
            </p>
          </motion.div>
        )}

        {/* Back Soon */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-primary text-xl font-serif"
        >
          {backSoon[language] || backSoon.en}
        </motion.p>

        {/* Decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mt-8"
        />

        {/* Contact info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground"
        >
          <Mail size={16} className="text-primary" />
          <span>contact@rentiasi.ro</span>
        </motion.div>
      </div>

      {/* Animated dots */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex gap-2">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{ 
              y: [0, -10, 0],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{ 
              duration: 1.5, 
              repeat: Infinity, 
              delay: i * 0.2 
            }}
            className="w-2 h-2 rounded-full bg-primary"
          />
        ))}
      </div>
    </div>
  );
};

export default MaintenancePage;
